import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  ExperienceState,
  GetCustomRequestsParams,
  GetExperienceBookingsParams,
  GetExperiencesParams,
  UpdateBookingStatusParams,
  UpdateCustomRequestStatusParams,
  CreatePaymentLinkParams,
  PaymentLinkItem,
} from "@/types/admin/experience-type";
import { experienceService } from "@/services/system-admin/experience.service";

const initialState: ExperienceState = {
  experiences: [],
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,

  selectedExperience: null,
  detailLoading: false,
  detailError: null,

  history: [],
  historyLoading: false,
  bookings: [],
   bookingsLoading: false,
   bookingsError: null,
   bookingsTotalCount: 0,
   bookingsCurrentPage: 1,
   bookingsPageSize: 20,

customRequests: [],
  customRequestsLoading: false,
  customRequestsError: null,
  customRequestsTotalCount: 0,
  customRequestsCurrentPage: 1,
  customRequestsPageSize: 20,

  paymentLinksByBooking: {},
  paymentLinksLoading: false,
  createPaymentLinkLoading: false,
  createPaymentLinkError: null,
};

// ─── Thunks ───────────────────────────────────────────────────────────────────

export const fetchExperiences = createAsyncThunk(
  "experiences/fetchExperiences",
  async (params: GetExperiencesParams) => {
    const response = await experienceService.getExperiences(params);
    return { data: response, page: params.page, pageSize: params.pageSize };
  }
);

export const fetchExperienceById = createAsyncThunk(
  "experiences/fetchExperienceById",
  async (id: string) => {
    return await experienceService.getExperienceById(id);
  }
);

export const fetchExperienceHistory = createAsyncThunk(
  "experiences/fetchExperienceHistory",
  async (id: string) => {
    return await experienceService.getExperienceHistory(id);
  }
);

export const startReview = createAsyncThunk(
  "experiences/startReview",
  async ({ id, adminUserId }: { id: string; adminUserId: string }) => {
    return await experienceService.startReview(id, adminUserId);
  }
);

export const approveExperience = createAsyncThunk(
  "experiences/approveExperience",
  async ({
    id,
    adminUserId,
    notes,
  }: {
    id: string;
    adminUserId: string;
    notes: string;
  }) => {
    return await experienceService.approveExperience(id, adminUserId, notes);
  }
);

export const requestChanges = createAsyncThunk(
  "experiences/requestChanges",
  async ({
    id,
    adminUserId,
    notes,
  }: {
    id: string;
    adminUserId: string;
    notes: string;
  }) => {
    return await experienceService.requestChanges(id, adminUserId, notes);
  }
);

export const rejectExperience = createAsyncThunk(
  "experiences/rejectExperience",
  async ({
    id,
    adminUserId,
    reason,
  }: {
    id: string;
    adminUserId: string;
    reason: string;
  }) => {
    return await experienceService.rejectExperience(id, adminUserId, reason);
  }
);

export const fetchExperienceBookings = createAsyncThunk(
  "experiences/fetchExperienceBookings",
  async (params: GetExperienceBookingsParams) => {
    const response = await experienceService.getExperienceBookings(params);
    return { data: response, page: params.page, pageSize: params.pageSize };
  }
);
export const updateBookingStatus = createAsyncThunk(
  "experiences/updateBookingStatus",
  async (params: UpdateBookingStatusParams) => {
    return await experienceService.updateBookingStatus(params);
  }
);
export const fetchCustomRequests = createAsyncThunk(
  "experiences/fetchCustomRequests",
  async (params: GetCustomRequestsParams) => {
    const response = await experienceService.getCustomRequests(params);
    return { data: response, page: params.page, pageSize: params.pageSize };
  }
);

export const updateCustomRequestStatus = createAsyncThunk(
  "experiences/updateCustomRequestStatus",
  async (params: UpdateCustomRequestStatusParams) => {
    return await experienceService.updateCustomRequestStatus(params);
  }
);

// ─── Payment Links ────────────────────────────────────────────────────────

export const fetchBookingPaymentLinks = createAsyncThunk(
  "experiences/fetchBookingPaymentLinks",
  async (bookingUid: string, thunkApi) => {
    try {
      const data = await experienceService.getBookingPaymentLinks(bookingUid);
      return { bookingUid, links: data };
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to fetch payment links",
      );
    }
  },
);

export const createBookingPaymentLink = createAsyncThunk(
  "experiences/createBookingPaymentLink",
  async (params: CreatePaymentLinkParams, thunkApi) => {
    try {
      const data = await experienceService.createBookingPaymentLink(params);
      return data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error?.message || "Failed to create payment link",
      );
    }
  },
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const experienceSlice = createSlice({
  name: "experiences",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedExperience: (state) => {
      state.selectedExperience = null;
      state.detailError = null;
      state.history = [];
    },
  },
  extraReducers: (builder) => {
    // ── fetchExperiences ──
    builder
      .addCase(fetchExperiences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
   .addCase(fetchExperiences.fulfilled, (state, action) => {
        state.loading = false;
        // API returns a flat array (not paginated object like customers)
        const list = Array.isArray(action.payload.data)
          ? action.payload.data
          : action.payload.data?.items || action.payload.data?.result?.items || [];
        state.experiences = list;
        state.currentPage = action.payload.page || 1;
        state.pageSize = action.payload.pageSize || 20;
        // Prefer a real total-record count embedded in the response, the same
        // way hotel-approve-slice reads `firstHotel.totalCount`. Falling back
        // to list.length under-reports whenever a page is full, which makes
        // Next/Previous look "stuck" since totalPages computes too low.
        state.totalCount =
          list[0]?.totalCount ?? list[0]?.totalRecordCount ?? list.length ?? 0;
      })
      .addCase(fetchExperiences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch experiences";
      });

    // ── fetchExperienceById ──
    builder
      .addCase(fetchExperienceById.pending, (state) => {
        state.detailLoading = true;
        state.detailError = null;
      })
      .addCase(fetchExperienceById.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedExperience = action.payload;
      })
      .addCase(fetchExperienceById.rejected, (state, action) => {
        state.detailLoading = false;
        state.detailError =
          action.error.message || "Failed to load experience";
      });

    // ── fetchExperienceHistory ──
    builder
      .addCase(fetchExperienceHistory.pending, (state) => {
        state.historyLoading = true;
      })
      .addCase(fetchExperienceHistory.fulfilled, (state, action) => {
        state.historyLoading = false;
        state.history = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchExperienceHistory.rejected, (state) => {
        state.historyLoading = false;
      });

     // ── fetchExperienceBookings ──
    builder
      .addCase(fetchExperienceBookings.pending, (state) => {
        state.bookingsLoading = true;
        state.bookingsError = null;
      })
     .addCase(fetchExperienceBookings.fulfilled, (state, action) => {
  state.bookingsLoading = false;
  state.bookings = action.payload.data.items ?? [];
  state.bookingsTotalCount = action.payload.data.totalCount ?? 0;
  state.bookingsCurrentPage = action.payload.page ?? state.bookingsCurrentPage;
  state.bookingsPageSize = action.payload.pageSize ?? state.bookingsPageSize;
})
      .addCase(fetchExperienceBookings.rejected, (state, action) => {
        state.bookingsLoading = false;
        state.bookingsError =
          action.error.message || "Failed to fetch experience bookings";
      });

      builder
      .addCase(updateBookingStatus.fulfilled, (state, action) => {
  const updated = action.payload;
  if (!updated?.bookingUid) return;
  const idx = state.bookings.findIndex((b) => b.bookingUid === updated.bookingUid);
  if (idx !== -1) {
    state.bookings[idx] = {
      ...state.bookings[idx],
      ...(updated.bookingStatus ? { bookingStatus: updated.bookingStatus } : {}),
      ...(updated.paymentStatus ? { paymentStatus: updated.paymentStatus } : {}),
    };
  }
});
// ── fetchCustomRequests ──
    builder
      .addCase(fetchCustomRequests.pending, (state) => {
        state.customRequestsLoading = true;
        state.customRequestsError = null;
      })
     .addCase(fetchCustomRequests.fulfilled, (state, action) => {
  state.customRequestsLoading = false;
  state.customRequests = action.payload.data.items ?? [];
  state.customRequestsTotalCount = action.payload.data.totalCount ?? 0;
  state.customRequestsCurrentPage = action.payload.page ?? state.customRequestsCurrentPage;
  state.customRequestsPageSize = action.payload.pageSize ?? state.customRequestsPageSize;
})
      .addCase(fetchCustomRequests.rejected, (state, action) => {
        state.customRequestsLoading = false;
        state.customRequestsError =
          action.error.message || "Failed to fetch custom requests";
      });

    // ── updateCustomRequestStatus ──
    builder.addCase(updateCustomRequestStatus.fulfilled, (state, action) => {
      const updated = action.payload;
      if (!updated?.uid) return;
      const idx = state.customRequests.findIndex((r) => r.uid === updated.uid);
      if (idx !== -1) {
        state.customRequests[idx] = {
          ...state.customRequests[idx],
          status: updated.status ?? state.customRequests[idx].status,
          responseNotes: updated.responseNotes ?? state.customRequests[idx].responseNotes,
        };
      }
    });



  // ── payment links ──
    builder
      .addCase(fetchBookingPaymentLinks.pending, (state) => {
        state.paymentLinksLoading = true;
      })
      .addCase(fetchBookingPaymentLinks.fulfilled, (state, action) => {
        state.paymentLinksLoading = false;
        state.paymentLinksByBooking[action.payload.bookingUid] =
          action.payload.links;
      })
      .addCase(fetchBookingPaymentLinks.rejected, (state) => {
        state.paymentLinksLoading = false;
      });

    builder
      .addCase(createBookingPaymentLink.pending, (state) => {
        state.createPaymentLinkLoading = true;
        state.createPaymentLinkError = null;
      })
      .addCase(createBookingPaymentLink.fulfilled, (state, action) => {
        state.createPaymentLinkLoading = false;
        const bookingUid = action.payload.bookingUid;
        const existing = state.paymentLinksByBooking[bookingUid] ?? [];
        const newLink: PaymentLinkItem = {
          paymentId: action.payload.paymentId,
          transactionId: action.payload.transactionId,
          bookingUid: action.payload.bookingUid,
          paymentType: action.payload.paymentType,
          amount: action.payload.amount,
          currency: action.payload.currency,
          paymentStatus: 0,
          paymentStatusText: "PENDING",
          paycenterReqId: action.payload.paycenterReqId,
          paymentPageUrl: action.payload.paymentPageUrl,
          createdAt: new Date().toISOString(),
        };
        state.paymentLinksByBooking[bookingUid] = [newLink, ...existing];
      })
      .addCase(createBookingPaymentLink.rejected, (state, action) => {
        state.createPaymentLinkLoading = false;
        state.createPaymentLinkError =
          action.error.message || "Failed to create payment link";
      });

    // ── status actions — optimistic update ──
   const statusFulfilled = (
      state: ExperienceState,
      action: { payload: { id?: string; status?: string } }
    ) => {
      const updated = action.payload;
      if (!updated?.id) return;
      const idx = state.experiences.findIndex((e) => e.id === updated.id);
      if (idx !== -1) {
        state.experiences[idx] = {
          ...state.experiences[idx],
          status: updated.status ?? state.experiences[idx].status,
        };
      }
      if (state.selectedExperience?.id === updated.id) {
        state.selectedExperience = {
          ...state.selectedExperience,
          status: updated.status ?? state.selectedExperience.status,
        } as typeof state.selectedExperience;
      }
    };

    builder.addCase(startReview.fulfilled, statusFulfilled);
    builder.addCase(approveExperience.fulfilled, statusFulfilled);
    builder.addCase(requestChanges.fulfilled, statusFulfilled);
    builder.addCase(rejectExperience.fulfilled, statusFulfilled);
  },
});

 

 

export const { clearError, clearSelectedExperience } = experienceSlice.actions;
export default experienceSlice.reducer;